import {
  makeAuthenticatedRequest,
  makeAuthenticatedRequestWithHandling,
} from "./api";

export const getAssessmentById = async (id) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/assessments/${id}`,
      "GET"
    );
    return response.json();
  } catch (error) {
    console.error("Error during getting assessments:", error);
    return { ok: false, error: error };
  }
};

export const getTroubleSpots = async (classroomId, studentId = "") => {
  try {
    // Construct the URL with the optional studentId query parameter
    let url = `/examAttempt/spots/${classroomId}`;

    if (studentId) {
      url += `?userId=${studentId}`;
    }

    const response = await makeAuthenticatedRequestWithHandling(url, "GET");
    return response.json();
  } catch (error) {
    console.error("Error during getting trouble spots:", error);
    return { ok: false, error: error };
  }
};

export const getAssessmentPreviewById = async (id) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/assessments/preview/${id}`,
      "GET"
    );
    return response.json();
  } catch (error) {
    console.error("Error during getting assessments:", error);
    return { ok: false, error: error };
  }
};

export const getAssessmentByCourseId = async (courseId) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/assessments/course/${courseId}`,
      "GET"
    );
    return response.json();
  } catch (error) {
    console.error("Error during getting assessments:", error);
    return { ok: false, error: error };
  }
};

export const createAssessmentByCourseId = async (
  courseId,
  data,
  { toastText }
) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/assessments/course/${courseId}`,
      "POST",
      data,
      toastText
    );
    return response.json();
  } catch (error) {
    console.error("Error during creating assessments:", error);
    throw new Error(error?.message || "Error during creating assessments");
  }
};

export const updateAssessment = async (id, formData) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/assessments/${id}`,
      "PATCH",
      formData
    );
    return response.json();
  } catch (error) {
    console.error("Error during getting assessments:", error);
    throw new Error(error?.message || "Error during updating assessments");
  }
};

export const deleteAssessmentById = async (id) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/assessments/${id}`,
      "DELETE"
    );
    return response;
  } catch (error) {
    console.error("Error during getting assessments:", error);
    return { ok: false, error: error };
  }
};

export const getAssessmentSection = async (assessmentId) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/assessmentSection/${assessmentId}`,
      "GET"
    );
    return response.json();
  } catch (error) {
    console.error("Error during getting assessments:", error);
    return { ok: false, error: error };
  }
};

export const getAssessmentSectionById = async (assessmentId, sectionId) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/assessmentSection/${assessmentId}/${sectionId}`,
      "GET"
    );
    return response.json();
  } catch (error) {
    console.error("Error during getting assessments:", error);
    return { ok: false, error: error };
  }
};

export const deleteAssessmentQuestionFromSection = async (
  assessmentId,
  sectionId,
  questionId
) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/assessmentQuestion/${assessmentId}/${sectionId}/${questionId}`,
      "DELETE"
    );
    if (!response.ok) {
      throw new Error("Failed to delete question");
    }
    return response.json();
  } catch (error) {
    console.error("Error during deleting assessment section:", error);
    throw error; // rethrow the error so it can be handled by the caller
  }
};
export const deleteAssessmentSection = async (assessmentId, sectionId) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/assessmentSection/${assessmentId}/${sectionId}`,
      "DELETE"
    );
    if (!response.ok) {
      throw new Error("Failed to delete section");
    }
    return response.json();
  } catch (error) {
    console.error("Error during deleting assessment section:", error);
    throw error; // rethrow the error so it can be handled by the caller
  }
};

export const createAssessmentSection = async (assessmentId, data) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/assessmentSection/${assessmentId}`,
      "POST",
      data
    );
    return await response.json();
  } catch (error) {
    console.error("Error during creating assessments:", error);
    return { ok: false, error: error };
  }
};

export const updateAssessmentSection = async (
  assessmentId,
  sectionId,
  data
) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/assessmentSection/${assessmentId}/${sectionId}`,
      "PATCH",
      data
    );
    return await response.json();
  } catch (error) {
    console.error("Error during creating assessments:", error);
    return { ok: false, error: error };
  }
};

export const reorderSections = async (assessmentId, data, toastText) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/assessments/${assessmentId}/reorder_sections`,
      "PATCH",
      data,
      toastText
    );
    return response;
  } catch (error) {
    console.error("Error during creating assessments:", error);
    return { ok: false, error: error };
  }
};

export const getBrowseDropDownsData = async (courseId) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/assessmentSection/course/${courseId}/dropDownsData`,
      "GET"
    );
    return response.json();
  } catch (error) {
    console.error("Error during getting assessments:", error);
    return { ok: false, error: error };
  }
};

export const getFilteredQuestions = async (
  courseId,
  data,
  pageNumber,
  pageSize
) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/assessmentQuestion/course/${courseId}/filterQuestions?pageNumber=${pageNumber}&pageSize=${pageSize}`,
      "POST",
      data
    );
    return response.json();
  } catch (error) {
    console.error("Error during creating assessments:", error);
    return { ok: false, error: error };
  }
};

export const addQuestionToSection = async (
  courseId,
  assessmentId,
  data,
  toastText
) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/assessmentSection/questions/${courseId}/${assessmentId}`,
      "POST",
      data,
      toastText
    );
    return response;
  } catch (error) {
    console.error("Error during creating assessments:", error);
    return { ok: false, error: error };
  }
};

export const addRulesToSection = async (assessmentId, sectionId, data) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/assessmentSection/${assessmentId}/${sectionId}`,
      "POST",
      data
    );
    return response;
  } catch (error) {
    console.error("Error during creating assessments:", error);
    return { ok: false, error: error };
  }
};

export const addQuestionToAssessment = async (
  assessmentId,
  sectionId,
  data
) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/assessmentQuestion/${assessmentId}/${sectionId}`,
      "POST",
      data
    );
    return response;
  } catch (error) {
    console.error("Error during creating assessments:", error);
    return { ok: false, error: error };
  }
};

export const addQuestionToPracticeSet = async (topicId, practiceId, data) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/practices/${topicId}/${practiceId}/question`,
      "POST",
      data
    );
    return response;
  } catch (error) {
    console.error("Error during creating assessments:", error);
    return { ok: false, error: error };
  }
};

export const addQuestionToTest = async (topicId, testId, data) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/tests/${topicId}/${testId}/question`,
      "POST",
      data
    );
    return response;
  } catch (error) {
    console.error("Error during creating assessments:", error);
    return { ok: false, error: error };
  }
};

export const createExamAttempt = async (data, classroomId, toastText) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/examAttempt/${classroomId}`,
      "POST",
      data,
      toastText
    );
    return await response?.json();
  } catch (error) {
    console.error("Error during creating assessments:", error);
    return { ok: false, error: error };
  }
};

export const getExamAttemptById = async (attemptId) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/examAttempt/${attemptId}`,
      "GET"
    );
    return await response.json();
  } catch (error) {
    console.error("Error during getting assessments:", error);
    return { ok: false, error: error };
  }
};

export const updateExamAttempt = async (data, attemptId, toastText) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/examAttempt/${attemptId}`,
      "PATCH",
      data,
      toastText
    );
    return await response?.json();
  } catch (error) {
    console.error("Error during creating assessments:", error);
    return { ok: false, error: error };
  }
};

export const deleteExamAttempt = async (attemptId) => {
  try {
    const response = await makeAuthenticatedRequestWithHandling(
      `/examAttempt/${attemptId}`,
      "DELETE"
    );
    return response;
  } catch (error) {
    console.error("Error during creating assessments:", error);
    return { ok: false, error: error };
  }
};

export const getPracticeOfTopic = async (topicId, practiceId) => {
  try {
    const URL = `/practices/${topicId}/${practiceId}`;
    return await makeAuthenticatedRequest(URL, "GET")
      .then((response) => {
        // Check if the request was successful (status code 200-299)
        if (!response.ok) {
          return { ok: false, error: response };
        }
        // Parse the response as JSON
        return response.json();
      })
      .then((data) => {
        // Handle the JSON data
        return { ok: true, data: data };
      })
      .catch((error) => {
        // Handle errors
        console.error("Fetch error:", error);
        return { ok: false, error: error };
      });
  } catch (error) {
    // Handle network errors or unexpected issues
    console.error("Error during getting classrooms:", error);
    return { ok: false, error: error };
  }
};

export const getTestOfTopic = async (topicId, practiceId) => {
  try {
    const URL = `/tests/${topicId}/${practiceId}`;
    return await makeAuthenticatedRequest(URL, "GET")
      .then((response) => {
        // Check if the request was successful (status code 200-299)
        if (!response.ok) {
          return { ok: false, error: response };
        }
        // Parse the response as JSON
        return response.json();
      })
      .then((data) => {
        // Handle the JSON data
        return { ok: true, data: data };
      })
      .catch((error) => {
        // Handle errors
        console.error("Fetch error:", error);
        return { ok: false, error: error };
      });
  } catch (error) {
    // Handle network errors or unexpected issues
    console.error("Error during getting classrooms:", error);
    return { ok: false, error: error };
  }
};
